/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Datos mock de empresas para gestión de usuarios
const empresasMock = [
  {
    id: 1,
    nombre: "TechCorp Solutions",
    nit: "900123456-7",
    email: "info@techcorp.com",
    telefono: "+57 300 123 4567",
    descripcion: "Empresa especializada en soluciones tecnológicas innovadoras para el sector empresarial",
    sector_empresa: "Tecnología",
    zona_estrategicas: "Zona Norte",
    tamaño_empresa: "Grande",
    area_operacion: "Nacional",
    pais_operacion: "Colombia",
    ciudad_operacion: "Bogotá",
    estado_empresa: "Activa",
    fecha_creacion: "2023-01-15",
  },
  {
    id: 2,
    nombre: "Green Energy S.A.S",
    nit: "900234567-8",
    email: "contacto@greenenergy.com",
    telefono: "+57 301 234 5678",
    descripcion: "Empresa líder en energías renovables y sostenibilidad ambiental",
    sector_empresa: "Energía",
    zona_estrategicas: "Zona Centro",
    tamaño_empresa: "Mediana",
    area_operacion: "Regional",
    pais_operacion: "Colombia",
    ciudad_operacion: "Medellín",
    estado_empresa: "Activa",
    fecha_creacion: "2022-11-20",
  },
  {
    id: 3,
    nombre: "Food Innovation Ltda",
    nit: "900345678-9",
    email: "ventas@foodinnov.com",
    telefono: "+57 302 345 6789",
    descripcion: "Innovación en productos alimenticios y desarrollo de nuevas tendencias culinarias",
    sector_empresa: "Alimentos",
    zona_estrategicas: "Zona Sur",
    tamaño_empresa: "Pequeña",
    area_operacion: "Local",
    pais_operacion: "Colombia",
    ciudad_operacion: "Cali",
    estado_empresa: "Pendiente",
    fecha_creacion: "2023-03-10",
  },
  {
    id: 4,
    nombre: "HealthCare Plus",
    nit: "900456789-0",
    email: "admin@healthcareplus.com",
    telefono: "+57 303 456 7890",
    descripcion: "Servicios de salud especializados con tecnología de vanguardia",
    sector_empresa: "Salud",
    zona_estrategicas: "Zona Este",
    tamaño_empresa: "Grande",
    area_operacion: "Nacional",
    pais_operacion: "Colombia",
    ciudad_operacion: "Barranquilla",
    estado_empresa: "Activa",
    fecha_creacion: "2021-09-01",
  },
  {
    id: 5,
    nombre: "EduTech Learning",
    nit: "900567890-1",
    email: "info@edutech.com",
    telefono: "+57 304 567 8901",
    descripcion: "Plataforma educativa digital para el aprendizaje del siglo XXI",
    sector_empresa: "Educación",
    zona_estrategicas: "Zona Oeste",
    tamaño_empresa: "Mediana",
    area_operacion: "Internacional",
    pais_operacion: "Colombia",
    ciudad_operacion: "Bucaramanga",
    estado_empresa: "Inactiva",
    fecha_creacion: "2022-01-30",
  },
  {
    id: 6,
    nombre: "Innovate Logistics",
    email: "contact@innovatelogistics.com",
    sector_empresa: "Transporte",
    zona_estrategicas: "Zona Norte",
    tamaño_empresa: "Grande",
    estado_empresa: "Activa",
    fecha_creacion: "2022-02-18",
  },
  {
    id: 7,
    nombre: "Constructora BuildRight",
    email: "proyectos@buildright.com",
    sector_empresa: "Construcción",
    zona_estrategicas: "Zona Sur",
    tamaño_empresa: "Grande",
    estado_empresa: "Activa",
    fecha_creacion: "2020-07-30",
  },
  {
    id: 8,
    nombre: "Marketing Digital Creativo",
    email: "ideas@marketingcreativo.com",
    sector_empresa: "Publicidad",
    zona_estrategicas: "Zona Centro",
    tamaño_empresa: "Pequeña",
    estado_empresa: "Pendiente",
    fecha_creacion: "2023-08-01",
  },
];

export default function useEmpresasTableData() {
  const Author = ({ image, name, email }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  // Typechecking props for the Author
  Author.propTypes = {
    image: PropTypes.string,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  };

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  // Typechecking props for the Job
  Job.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Activa: { color: "success", label: "Activa" },
      Pendiente: { color: "warning", label: "Pendiente" },
      Inactiva: { color: "error", label: "Inactiva" },
    };

    const config = statusConfig[status] || { color: "secondary", label: status };
    
    return (
      <MDBox ml={-1}>
        <MDBadge badgeContent={config.label} color={config.color} variant="gradient" size="sm" />
      </MDBox>
    );
  };

  const initialRows = empresasMock.map((empresa) => ({
    empresa: <Author image={empresa.image} name={empresa.nombre} email={empresa.email} />,
    sector: <Job title={empresa.sector_empresa} description={empresa.zona_estrategicas} />,
    tamaño: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {empresa.tamaño_empresa}
      </MDTypography>
    ),
    estado: getStatusBadge(empresa.estado_empresa),
    accion: "action-placeholder", // Se maneja en el componente padre
    original: empresa, // Datos originales para edición/eliminación
  }));

  const [rows, setRows] = useState(initialRows);

  const columns = [
    { Header: "empresa", accessor: "empresa", width: "25%", align: "left" },
    { Header: "sector", accessor: "sector", align: "left" },
    { Header: "tamaño", accessor: "tamaño", align: "center" },
    { Header: "estado", accessor: "estado", align: "center" },
    { Header: "acción", accessor: "accion", align: "center" },
  ];

  return {
    columns,
    rows,
    setRows,
  };
}

export { useEmpresasTableData }; 